import './agent-service-form.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import { buildAgentServiceFormSchema } from './partner-service-form.schema';
import { Spinner } from 'react-bootstrap';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { useLocation, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { useAppContext } from '../../utils/content.util';
import { createAgentApplication, AgentValidationError } from '../../services/agentApplications.service';
import { getDealer } from '../../services/dealers.service';
import { Dealer } from '../../models/dealer.model';
import { onError } from '../../utils/error.util';
import { CreateApplicationRequest, LeadSource } from '../../models/application.model';
import { CircularProgressComponent } from '../../components/admin/circular-progress.component';
import { CompanyConfig } from '../../company.config';
import { isDev } from '../../utils/utils';
import { AwsCognitoUser } from '../../models/user.model';
import { POSITION_OPTIONS } from '../../models/role.model';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DC','DE','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

const BIRTHDATE_MAX = new Date().toISOString().slice(0, 10);
const BIRTHDATE_MIN = `${new Date().getFullYear() - 120}-01-01`;

const US_CITIZEN_TOOLTIP = 'A person who was born in the U.S. or has become a citizen through the naturalization process.';
const US_RESIDENT_TOOLTIP = "A non-citizen who has been granted authorization to live and work in the U.S. permanently. Also known as a 'green card holder.'";
const INFO_ICON_STYLE = { marginLeft: '8px', fontSize: '16px', color: '#6c757d', cursor: 'help' } as const;

function StateSelect({
  id, name, value, onChange, onBlur, required,
}: {
  id: string; name: string; value: string;
  onChange: React.ChangeEventHandler<HTMLSelectElement>;
  onBlur: React.FocusEventHandler<HTMLSelectElement>;
  required?: boolean;
}) {
  return (
    <select id={id} name={name} value={value} onChange={onChange} onBlur={onBlur} required={required}>
      <option value="">Choose</option>
      {US_STATES.map((s) => <option key={s}>{s}</option>)}
    </select>
  );
}

function parseNum(s: string | number | undefined): number {
  const n = parseFloat(String(s ?? '').replace(/[^0-9.-]/g, ''));
  return isNaN(n) ? 0 : n;
}

function formatPhone(s: string): string {
  const d = s.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatSsn(s: string): string {
  const d = s.replace(/\D/g, '').slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

function getAnalystName(user?: AwsCognitoUser): string {
  if (!user) return '';
  if (user['custom:position'] !== POSITION_OPTIONS.analyst) return '';
  return `${user.given_name ?? ''} ${user.family_name ?? ''}`.trim();
}

function getCreatedBy(user?: AwsCognitoUser): string {
  if (!user) return '';
  return `${user.given_name ?? ''} ${user.family_name ?? ''}`.trim();
}

interface InlineServiceFormProps {
  leadSource: LeadSource;
}

interface FormValues {
  firstName: string; middleName: string; lastName: string; suffix: string;
  primaryPhoneNumber: string; primaryPhoneType: string;
  emailAddress: string; maritalStatus: string;
  ssn: string; birthdate: string;

  residentialAddressLine1: string; residentialAddressLine2: string;
  residentialCity: string; residentialState: string; residentialZipCode: string;
  yearsAtResidentialAddress: string; monthsAtResidentialAddress: string;
  statusAtResidentialAddress: string; monthlyPayment: string;
  previousAddressLine1: string; previousAddressLine2: string;
  previousCity: string; previousState: string; previousZipCode: string;
  previousYearsAtAddress: string; previousMonthsAtAddress: string;
  isRegistrationAddress: string; registrationAddress: string;
  isMailingAddress: string; mailingAddress: string;

  retirementIncome: string; retirementYears: string; retirementMonths: string;
  employerName: string; employerPhoneNumber: string;
  occupationTitle: string; grossMonthlyIncome: string;
  employerAddressLine1: string; employerCity: string; employerState: string; employerZipCode: string;
  employedYears: string; employedMonths: string;
  previousEmployerName: string; previousOccupationTitle: string;
  previousGrossMonthlyIncome: string; previousEmployedYears: string; previousEmployedMonths: string;
  additionalIncomeSource: string; additionalMonthlyIncome: string; additionalIncomeSourceOther: string;
  isRetired: boolean; isEmployed: boolean; isAdditionalIncome: boolean;

  haveCoApplicant: boolean;
  coapplicantFirstName: string; coapplicantMiddleName: string;
  coapplicantLastName: string; coapplicantSuffix: string;
  coapplicantPrimaryPhoneNumber: string; coapplicantPrimaryPhoneType: string;
  coapplicantEmailAddress: string; coapplicantMaritalStatus: string;
  coapplicantSsn: string; coapplicantBirthdate: string;
  coapplicantResidentialAddressLine1: string;
  coapplicantResidentialCity: string; coapplicantResidentialState: string; coapplicantResidentialZipCode: string;
  coapplicantYearsAtResidentialAddress: string; coapplicantMonthsAtResidentialAddress: string;
  coapplicantStatusAtResidentialAddress: string; coapplicantMonthlyPayment: string;
  coapplicantPreviousAddressLine1: string;
  coapplicantPreviousCity: string; coapplicantPreviousState: string; coapplicantPreviousZipCode: string;
  coapplicantPreviousYearsAtAddress: string; coapplicantPreviousMonthsAtAddress: string;
  coapplicantRetirementIncome: string; coapplicantRetirementYears: string; coapplicantRetirementMonths: string;
  coapplicantEmployerName: string; coapplicantEmployerPhoneNumber: string;
  coapplicantOccupationTitle: string; coapplicantGrossMonthlyIncome: string;
  coapplicantEmployerAddressLine1: string; coapplicantEmployerCity: string;
  coapplicantEmployerState: string; coapplicantEmployerZipCode: string;
  coapplicantEmployedYears: string; coapplicantEmployedMonths: string;
  coapplicantPreviousEmployerName: string; coapplicantPreviousOccupationTitle: string;
  coapplicantPreviousGrossMonthlyIncome: string; coapplicantPreviousEmployedYears: string; coapplicantPreviousEmployedMonths: string;
  coapplicantAdditionalIncomeSource: string; coapplicantAdditionalMonthlyIncome: string; coapplicantAdditionalIncomeSourceOther: string;
  coapplicantIsRetired: boolean; coapplicantIsEmployed: boolean; coapplicantIsAdditionalIncome: boolean;

  applicationSource: string;
  partner: string; partnerAccountId: string; partnerRep: string; partnerRepEmail: string;
  sellerType: string; sellerName: string; sellerPhoneNumber: string; sellerContact: string; dealerName: string;
  collateralType: string; unitStatus: string;
  vehicleYear: string; vehicleMake: string; vehicleModel: string;
  vehicleSalesPrice: string; vehicleDownPayment: string;
  gps: string; theft: string; tireWheel: string; vsc: string;
  miscDescription: string; miscCost: string;

  referralSource: string; referralSourceOther: string;
  notes: string;

  usCitizen: boolean; usResident: boolean;
  applicantAgree: boolean; applicantSignature: string;
  coApplicantAgree: boolean; coApplicantUsCitizen: boolean; coApplicantUsResident: boolean; coApplicantSignature: string;

  createdBy: string; phoneNumberCallback: string;
  applicantResidentialAddressLine1: string;
  applicantResidentialCity: string; applicantResidentialState: string; applicantResidentialZipCode: string;
  dealerId: string;
  customAttributes: Record<string, string>;
}

export default function InlineServiceForm({ leadSource }: InlineServiceFormProps) {
  const { currentUser, isAuthenticated } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const isPhone = location.pathname === '/phone/partner-service-form';

  const { dealerId, dealerLocation, dealerStock, analyticName } = useParams();
  const [searchParams] = useSearchParams();

  const fname = searchParams.get('fname') ?? '';
  const lname = searchParams.get('lname') ?? '';
  const email = searchParams.get('email') ?? '';
  const vehicleYear = searchParams.get('vyr') ?? '';
  const vehicleMake = searchParams.get('vmk') ?? '';
  const vehicleModel = searchParams.get('vmd') ?? '';
  const queryStock = searchParams.get('stock') ?? '';
  const queryLocation = searchParams.get('location') ?? '';

  const partnerParam = (() => {
    const p = (searchParams.get('partner') ?? '').trim().slice(0, 40);
    return p;
  })();
  const accountIdParam = (() => {
    const a = (searchParams.get('accountId') ?? searchParams.get('acct') ?? '').trim().slice(0, 18);
    return /^[A-Za-z0-9]{15,18}$/.test(a) ? a : '';
  })();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isCreditAppSubmitting, setIsCreditAppSubmitting] = useState(false);
  const pendingSubmitValuesRef = useRef<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dealer, setDealer] = useState<Dealer | undefined>(undefined);
  const [showReview, setShowReview] = useState(false);
  const [customAttributes] = useState<Record<string, string>>({});

  useEffect(() => {
    async function onLoad() {
      if (!dealerId || isNaN(Number(dealerId))) { setIsLoading(false); return; }
      try {
        const response = await getDealer(dealerId);
        setDealer(response);
      } catch {
        navigate('/');
      }
      setIsLoading(false);
    }
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId]);

  const initialValues = useMemo((): FormValues => ({
    firstName: fname, middleName: '', lastName: lname, suffix: '',
    primaryPhoneNumber: '', primaryPhoneType: '',
    emailAddress: email, maritalStatus: '',
    ssn: '', birthdate: '',

    residentialAddressLine1: '', residentialAddressLine2: '',
    residentialCity: '', residentialState: '', residentialZipCode: '',
    yearsAtResidentialAddress: '', monthsAtResidentialAddress: '',
    statusAtResidentialAddress: '', monthlyPayment: '',
    previousAddressLine1: '', previousAddressLine2: '',
    previousCity: '', previousState: '', previousZipCode: '',
    previousYearsAtAddress: '', previousMonthsAtAddress: '',
    isRegistrationAddress: '', registrationAddress: '',
    isMailingAddress: '', mailingAddress: '',

    retirementIncome: '', retirementYears: '', retirementMonths: '',
    employerName: '', employerPhoneNumber: '',
    occupationTitle: '', grossMonthlyIncome: '',
    employerAddressLine1: '', employerCity: '', employerState: '', employerZipCode: '',
    employedYears: '', employedMonths: '',
    previousEmployerName: '', previousOccupationTitle: '',
    previousGrossMonthlyIncome: '', previousEmployedYears: '', previousEmployedMonths: '',
    additionalIncomeSource: '', additionalMonthlyIncome: '', additionalIncomeSourceOther: '',
    isRetired: false, isEmployed: false, isAdditionalIncome: false,

    haveCoApplicant: false,
    coapplicantFirstName: '', coapplicantMiddleName: '',
    coapplicantLastName: '', coapplicantSuffix: '',
    coapplicantPrimaryPhoneNumber: '', coapplicantPrimaryPhoneType: '',
    coapplicantEmailAddress: '', coapplicantMaritalStatus: '',
    coapplicantSsn: '', coapplicantBirthdate: '',
    coapplicantResidentialAddressLine1: '',
    coapplicantResidentialCity: '', coapplicantResidentialState: '', coapplicantResidentialZipCode: '',
    coapplicantYearsAtResidentialAddress: '', coapplicantMonthsAtResidentialAddress: '',
    coapplicantStatusAtResidentialAddress: '', coapplicantMonthlyPayment: '',
    coapplicantPreviousAddressLine1: '',
    coapplicantPreviousCity: '', coapplicantPreviousState: '', coapplicantPreviousZipCode: '',
    coapplicantPreviousYearsAtAddress: '', coapplicantPreviousMonthsAtAddress: '',
    coapplicantRetirementIncome: '', coapplicantRetirementYears: '', coapplicantRetirementMonths: '',
    coapplicantEmployerName: '', coapplicantEmployerPhoneNumber: '',
    coapplicantOccupationTitle: '', coapplicantGrossMonthlyIncome: '',
    coapplicantEmployerAddressLine1: '', coapplicantEmployerCity: '',
    coapplicantEmployerState: '', coapplicantEmployerZipCode: '',
    coapplicantEmployedYears: '', coapplicantEmployedMonths: '',
    coapplicantPreviousEmployerName: '', coapplicantPreviousOccupationTitle: '',
    coapplicantPreviousGrossMonthlyIncome: '', coapplicantPreviousEmployedYears: '', coapplicantPreviousEmployedMonths: '',
    coapplicantAdditionalIncomeSource: '', coapplicantAdditionalMonthlyIncome: '', coapplicantAdditionalIncomeSourceOther: '',
    coapplicantIsRetired: false, coapplicantIsEmployed: false, coapplicantIsAdditionalIncome: false,

    applicationSource: partnerParam || 'Direct',
    partner: partnerParam,
    partnerAccountId: accountIdParam,
    partnerRep: '',
    partnerRepEmail: '',
    sellerType: 'Dealer',
    sellerName: dealer?.companyName ?? '',
    sellerPhoneNumber: dealer?.phone ?? '',
    sellerContact: '',
    dealerName: dealer?.companyName ?? '',
    collateralType: '',
    unitStatus: '',
    vehicleYear,
    vehicleMake,
    vehicleModel,
    vehicleSalesPrice: '',
    vehicleDownPayment: '',
    gps: '', theft: '', tireWheel: '', vsc: '',
    miscDescription: '', miscCost: '',

    referralSource: dealerId ? 'Dealership' : partnerParam ? 'Partner' : '',
    referralSourceOther: '',
    notes: '',

    usCitizen: false, usResident: false,
    applicantAgree: false, applicantSignature: '',
    coApplicantAgree: false, coApplicantUsCitizen: false, coApplicantUsResident: false, coApplicantSignature: '',

    createdBy: isAuthenticated && currentUser ? getCreatedBy(currentUser) : '',
    phoneNumberCallback: '',
    applicantResidentialAddressLine1: '',
    applicantResidentialCity: '', applicantResidentialState: '', applicantResidentialZipCode: '',
    dealerId: dealerId ?? '',
    customAttributes: {},
  }), [
    fname, lname, email, vehicleYear, vehicleMake, vehicleModel,
    partnerParam, accountIdParam, dealer, dealerId, isAuthenticated, currentUser,
  ]);

  function handlePreSubmit(values: FormValues) {
    pendingSubmitValuesRef.current = values as unknown as Record<string, unknown>;
    setShowReview(true);
  }

  const handleConfirmSubmit = useCallback(async () => {
    setShowReview(false);
    setIsCreditAppSubmitting(true);
    const fv = pendingSubmitValuesRef.current as any;
    try {
      const token = await executeRecaptcha?.('agent_application') ?? '';
      const payload: CreateApplicationRequest = {
        ...(fv as any),
        haveCoApplicant: fv.haveCoApplicant === true,
        dealerId: dealer?.id,
        dealerName: dealer?.companyName || fv.dealerName,
        dealerLocation: dealerLocation || queryLocation,
        dealerStock: dealerStock || queryStock,
        analyticName,
        token,
        customAttributes,
        leadSource,
        analystName: getAnalystName(currentUser),
      };

      await createAgentApplication(payload);

      if (currentUser) {
        navigate('/service-forms');
      } else if (dealerId) {
        window.location.href = CompanyConfig.dealerFormUrl;
      } else {
        window.location.href = CompanyConfig.formSubmittedUrl;
      }
    } catch (err) {
      setIsCreditAppSubmitting(false);
      pendingSubmitValuesRef.current = null;
      if (err instanceof AgentValidationError) {
        alert(err.errors.join('\n'));
      } else {
        onError(err);
        alert('Something wrong. Try to start over.');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeRecaptcha, dealer, dealerLocation, queryLocation, dealerStock, queryStock, analyticName, customAttributes, leadSource, currentUser, dealerId]);

  const validationSchema = useMemo(() => buildAgentServiceFormSchema(isPhone), [isPhone]);

  if (!partnerParam) {
    return (
      <div className="asf-page">
        <div className="asf-form-topbar">
          <img
            className="asf-form-topbar-logo"
            src="https://www.trailersolutionsfl.com/images/tsf-logo-blue.png"
            alt="Trailer Solutions FL"
          />
        </div>
        <div className="asf-card asf-partner-required">
          <h3>A TSF partner is required to complete this form. Please contact TSF for more information.</h3>
          <a className="asf-btn" href={CompanyConfig.contactUrl} target="_blank" rel="noopener noreferrer">Contact Us</a>
        </div>
      </div>
    );
  }

  if (isLoading) return <CircularProgressComponent />;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handlePreSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, submitCount, handleChange, handleBlur, setFieldValue, setValues }) => {
        const phoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          setFieldValue(e.target.name, formatPhone(e.target.value));
        };
        const ssnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          setFieldValue(e.target.name, formatSsn(e.target.value));
        };
        const moneyFocus = (e: React.FocusEvent<HTMLInputElement>) => {
          const n = parseNum(e.target.value);
          setFieldValue(e.target.name, n === 0 ? '' : String(n));
        };
        const moneyBlur = (e: React.FocusEvent<HTMLInputElement>) => {
          const n = parseNum(e.target.value);
          setFieldValue(e.target.name, n === 0 ? '' : n.toLocaleString('en-US', { maximumFractionDigits: 2 }));
          handleBlur(e);
        };
        const err = (name: keyof FormValues) => {
          const e = errors[name] as string | undefined;
          return (touched[name] || submitCount > 0) && e
            ? <span className="asf-field-error">{e}</span>
            : null;
        };
        const ic = (name: keyof FormValues) =>
          (touched[name] || submitCount > 0) && errors[name] ? 'asf-invalid' : undefined;
        const backendCapped = parseNum(values.theft) + parseNum(values.tireWheel) + parseNum(values.vsc) + parseNum(values.miscCost);
        const salesPrice = parseNum(values.vehicleSalesPrice);
        const backendExceeds = salesPrice > 0 && backendCapped > salesPrice * 0.25 + 0.005;
        const backendExceedsZero = salesPrice === 0 && backendCapped > 0;
        const showBackendAlert = backendExceeds || backendExceedsZero;
        const totalRequested = Math.max(0, salesPrice + parseNum(values.gps) + backendCapped - parseNum(values.vehicleDownPayment));
        const totalFmt = '$' + totalRequested.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const partnerLabel = values.partner || '';

        return (
          <div className="asf-page">
            <div className="asf-form-topbar">
              <img
                className="asf-form-topbar-logo"
                src="https://www.trailersolutionsfl.com/images/tsf-logo-blue.png"
                alt="Trailer Solutions FL"
              />
              <div className="asf-stip-callout asf-form-topbar">
                <div className="asf-stip-callout-text">
                  <div className="asf-stip-callout-title">Required Buyer Stipulations</div>
                  <p>Make sure the buyer has the required documents ready before submitting.</p>
                </div>
                <div className="asf-stip-callout-actions">
                  <a className="asf-stip-btn asf-stip-btn-primary" href={`${CompanyConfig.website}/stipulation-requirements.html`} target="_blank" rel="noopener noreferrer">View Checklist</a>
                  <a className="asf-stip-btn asf-stip-btn-secondary" href={`${CompanyConfig.website}/stipulation-requirements.pdf`} target="_blank" rel="noopener noreferrer">Download PDF</a>
                </div>
              </div>
            </div>

            <details className="asf-about-form">
              <summary>About this form &amp; privacy</summary>
              <div className="asf-about-form-content">
                <p>This form is provided by Trailer Solutions FL (TSF), a third-party financial services provider. By submitting, the buyer acknowledges their information will be sent directly to TSF for processing. TSF will share the information with its affiliated lenders for the purpose of evaluating financing options. Submission of this form does not constitute a loan approval or offer of credit. It is intended only to begin the process of exploring available financing.</p>
                <div className="asf-disc-links">
                  <a href={CompanyConfig.privacyUrl} target="_blank" rel="noreferrer">Privacy Policy</a>
                  <a href={CompanyConfig.termsUrl} target="_blank" rel="noreferrer">Terms of Use</a>
                  <a href={CompanyConfig.contactUrl} target="_blank" rel="noreferrer">Contact TSF</a>
                </div>
                <div className="asf-disc-address">{CompanyConfig.name} &middot; {CompanyConfig.address.addressLine1}, {CompanyConfig.address.city}, {CompanyConfig.address.state} {CompanyConfig.address.zipCode}</div>
              </div>
            </details>

            <Form id="agentApplicationForm" noValidate autoComplete="off">

              {/* ── Phone-only fields ── */}
              {isPhone && (
                <div className="asf-card asf-phone-card">
                  <h2 className="asf-section">Phone Submission</h2>
                  <div className="asf-grid asf-cols-2">
                    <div>
                      <label htmlFor="createdBy">Team Member</label>
                      <input id="createdBy" name="createdBy" type="text" value={values.createdBy} onChange={handleChange} onBlur={handleBlur} readOnly={!!isAuthenticated} />
                    </div>
                    <div>
                      <label htmlFor="phoneNumberCallback">Phone Number Callback</label>
                      <div className="asf-input-group">
                        <span className="asf-input-prefix">+1</span>
                        <input id="phoneNumberCallback" name="phoneNumberCallback" type="tel" value={values.phoneNumberCallback} onChange={phoneChange} onBlur={handleBlur} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Buyer Information ── */}
              <div className="asf-card">
                <h2 className="asf-section">Buyer Information</h2>

                <div className="asf-grid asf-cols-4">
                  <div>
                    <label htmlFor="firstName">First Name</label>
                    <input id="firstName" name="firstName" type="text" className={ic('firstName')} required value={values.firstName} onChange={handleChange} onBlur={handleBlur} />
                    {err('firstName')}
                  </div>
                  <div>
                    <label htmlFor="middleName">Middle Name</label>
                    <input id="middleName" name="middleName" type="text" value={values.middleName} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div>
                    <label htmlFor="lastName">Last Name</label>
                    <input id="lastName" name="lastName" type="text" className={ic('lastName')} required value={values.lastName} onChange={handleChange} onBlur={handleBlur} />
                    {err('lastName')}
                  </div>
                  <div>
                    <label htmlFor="suffix">Suffix</label>
                    <select id="suffix" name="suffix" value={values.suffix} onChange={handleChange} onBlur={handleBlur}>
                      <option value="">Choose</option>
                      <option>Jr.</option><option>Sr.</option><option>II</option><option>III</option><option>IV</option><option>V</option>
                    </select>
                  </div>
                </div>

                <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="primaryPhoneNumber">Phone Number</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">+1</span>
                      <input id="primaryPhoneNumber" name="primaryPhoneNumber" type="tel" className={ic('primaryPhoneNumber')} required value={values.primaryPhoneNumber} onChange={phoneChange} onBlur={handleBlur} />
                    </div>
                    {err('primaryPhoneNumber')}
                  </div>
                  <div>
                    <label htmlFor="primaryPhoneType">Phone Number Type</label>
                    <select id="primaryPhoneType" name="primaryPhoneType" className={ic('primaryPhoneType')} required value={values.primaryPhoneType} onChange={handleChange} onBlur={handleBlur}>
                      <option value="">Choose</option>
                      <option value="Mobile">Mobile</option>
                      <option value="Home">Home</option>
                    </select>
                    {err('primaryPhoneType')}
                  </div>
                </div>

                <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="emailAddress">E-Mail Address</label>
                    <input id="emailAddress" name="emailAddress" type="email" className={ic('emailAddress')} required value={values.emailAddress} onChange={handleChange} onBlur={handleBlur} />
                    {err('emailAddress')}
                  </div>
                  <div>
                    <label htmlFor="maritalStatus">Marital Status</label>
                    <select id="maritalStatus" name="maritalStatus" className={ic('maritalStatus')} required value={values.maritalStatus} onChange={handleChange} onBlur={handleBlur}>
                      <option value="">Choose</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Separated">Separated</option>
                    </select>
                    {err('maritalStatus')}
                  </div>
                </div>

                <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="ssn">SSN</label>
                    <input id="ssn" name="ssn" type="password" maxLength={11} className={ic('ssn')} required value={values.ssn} onChange={ssnChange} onBlur={handleBlur} />
                    {err('ssn')}
                  </div>
                  <div>
                    <label htmlFor="birthdate">Birth Date</label>
                    <input id="birthdate" name="birthdate" type="date" min={BIRTHDATE_MIN} max={BIRTHDATE_MAX} className={ic('birthdate')} required value={values.birthdate} onChange={handleChange} onBlur={handleBlur} />
                    {err('birthdate')}
                  </div>
                </div>
              </div>

              {/* ── Residential Information ── */}
              <div className="asf-card">
                <h2 className="asf-section">Residential Information</h2>

                <div className="asf-grid">
                  <div>
                    <label htmlFor="residentialAddressLine1">Physical Address</label>
                    <input id="residentialAddressLine1" name="residentialAddressLine1" type="text" className={ic('residentialAddressLine1')} required value={values.residentialAddressLine1} onChange={handleChange} onBlur={handleBlur} />
                    {err('residentialAddressLine1')}
                  </div>
                  <div>
                    <label htmlFor="residentialAddressLine2">Address Line 2 <span className="asf-hint">(apt, unit, suite)</span></label>
                    <input id="residentialAddressLine2" name="residentialAddressLine2" type="text" value={values.residentialAddressLine2} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                </div>

                <div className="asf-grid asf-cols-3" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="residentialCity">City</label>
                    <input id="residentialCity" name="residentialCity" type="text" className={ic('residentialCity')} required value={values.residentialCity} onChange={handleChange} onBlur={handleBlur} />
                    {err('residentialCity')}
                  </div>
                  <div>
                    <label htmlFor="residentialState">State</label>
                    <StateSelect id="residentialState" name="residentialState" required value={values.residentialState} onChange={handleChange} onBlur={handleBlur} />
                    {err('residentialState')}
                  </div>
                  <div>
                    <label htmlFor="residentialZipCode">Zip</label>
                    <input id="residentialZipCode" name="residentialZipCode" type="text" inputMode="decimal" maxLength={5} className={ic('residentialZipCode')} required value={values.residentialZipCode} onChange={handleChange} onBlur={handleBlur} />
                    {err('residentialZipCode')}
                  </div>
                </div>

                <h3 className="asf-subsection">Time at Residence</h3>
                <div className="asf-grid asf-cols-4">
                  <div>
                    <label htmlFor="yearsAtResidentialAddress">Years</label>
                    <input id="yearsAtResidentialAddress" name="yearsAtResidentialAddress" type="text" inputMode="decimal" className={ic('yearsAtResidentialAddress')} required value={values.yearsAtResidentialAddress} onChange={handleChange} onBlur={handleBlur} />
                    {err('yearsAtResidentialAddress')}
                  </div>
                  <div>
                    <label htmlFor="monthsAtResidentialAddress">Months</label>
                    <input id="monthsAtResidentialAddress" name="monthsAtResidentialAddress" type="text" inputMode="decimal" maxLength={2} className={ic('monthsAtResidentialAddress')} required value={values.monthsAtResidentialAddress} onChange={handleChange} onBlur={handleBlur} />
                    {err('monthsAtResidentialAddress')}
                  </div>
                  <div>
                    <label htmlFor="statusAtResidentialAddress">Status</label>
                    <select id="statusAtResidentialAddress" name="statusAtResidentialAddress" className={ic('statusAtResidentialAddress')} required value={values.statusAtResidentialAddress} onChange={handleChange} onBlur={handleBlur}>
                      <option value="">Choose</option>
                      <option value="Own/Buying">Own/Buying</option>
                      <option value="Rent">Rent</option>
                      <option value="with Relatives">with Relatives</option>
                      <option value="with Friends">with Friends</option>
                    </select>
                    {err('statusAtResidentialAddress')}
                  </div>
                  <div>
                    <label htmlFor="monthlyPayment">Mortgage/Rent Amount</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="monthlyPayment" name="monthlyPayment" type="text" inputMode="decimal" className={ic('monthlyPayment')} required value={values.monthlyPayment} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                    {err('monthlyPayment')}
                  </div>
                </div>

                <h4 className="asf-mini-section">Previous Address <span className="asf-hint">(required if less than 2 years at the current address above)</span></h4>
                <div className="asf-grid">
                  <div>
                    <label htmlFor="previousAddressLine1">Previous Address</label>
                    <input id="previousAddressLine1" name="previousAddressLine1" type="text" value={values.previousAddressLine1} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div>
                    <label htmlFor="previousAddressLine2">Previous Address Line 2</label>
                    <input id="previousAddressLine2" name="previousAddressLine2" type="text" value={values.previousAddressLine2} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                </div>
                <div className="asf-grid asf-cols-3" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="previousCity">City</label>
                    <input id="previousCity" name="previousCity" type="text" value={values.previousCity} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div>
                    <label htmlFor="previousState">State</label>
                    <StateSelect id="previousState" name="previousState" value={values.previousState} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div>
                    <label htmlFor="previousZipCode">Zip</label>
                    <input id="previousZipCode" name="previousZipCode" type="text" inputMode="decimal" maxLength={5} value={values.previousZipCode} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                </div>
                <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="previousYearsAtAddress">Years at Previous Address</label>
                    <input id="previousYearsAtAddress" name="previousYearsAtAddress" type="text" inputMode="decimal" className={ic('previousYearsAtAddress')} value={values.previousYearsAtAddress} onChange={handleChange} onBlur={handleBlur} />
                    {err('previousYearsAtAddress')}
                  </div>
                  <div>
                    <label htmlFor="previousMonthsAtAddress">Months at Previous Address</label>
                    <input id="previousMonthsAtAddress" name="previousMonthsAtAddress" type="text" inputMode="decimal" maxLength={2} className={ic('previousMonthsAtAddress')} value={values.previousMonthsAtAddress} onChange={handleChange} onBlur={handleBlur} />
                    {err('previousMonthsAtAddress')}
                  </div>
                </div>
              </div>

              {/* ── Employment Information ── */}
              <div className="asf-card">
                <h2 className="asf-section">Employment Information</h2>
                <p className="asf-form-note">Fill in any sections that apply to the buyer. At least one income source is required.</p>

                <h3 className="asf-subsection">Retirement / Disability Income</h3>
                <div className="asf-grid asf-cols-3">
                  <div>
                    <label htmlFor="retirementIncome">Monthly Income</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="retirementIncome" name="retirementIncome" type="text" inputMode="decimal" value={values.retirementIncome} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="retirementYears">Years Receiving</label>
                    <input id="retirementYears" name="retirementYears" type="text" inputMode="decimal" className={ic('retirementYears')} value={values.retirementYears} onChange={handleChange} onBlur={handleBlur} />
                    {err('retirementYears')}
                  </div>
                  <div>
                    <label htmlFor="retirementMonths">Months Receiving</label>
                    <input id="retirementMonths" name="retirementMonths" type="text" inputMode="decimal" maxLength={2} className={ic('retirementMonths')} value={values.retirementMonths} onChange={handleChange} onBlur={handleBlur} />
                    {err('retirementMonths')}
                  </div>
                </div>

                <h3 className="asf-subsection">Current Employment</h3>
                <div className="asf-grid asf-cols-2">
                  <div>
                    <label htmlFor="employerName">Employer Name</label>
                    <input id="employerName" name="employerName" type="text" maxLength={100} value={values.employerName} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div>
                    <label htmlFor="employerPhoneNumber">Employer Phone</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">+1</span>
                      <input id="employerPhoneNumber" name="employerPhoneNumber" type="tel" value={values.employerPhoneNumber} onChange={phoneChange} onBlur={handleBlur} />
                    </div>
                  </div>
                </div>
                <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="occupationTitle">Occupation / Title</label>
                    <input id="occupationTitle" name="occupationTitle" type="text" value={values.occupationTitle} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div>
                    <label htmlFor="grossMonthlyIncome">Gross Monthly Income</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="grossMonthlyIncome" name="grossMonthlyIncome" type="text" inputMode="decimal" value={values.grossMonthlyIncome} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                  </div>
                </div>
                <div className="asf-grid" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="employerAddressLine1">Employer Address</label>
                    <input id="employerAddressLine1" name="employerAddressLine1" type="text" maxLength={100} value={values.employerAddressLine1} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                </div>
                <div className="asf-grid asf-cols-3" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="employerCity">City</label>
                    <input id="employerCity" name="employerCity" type="text" maxLength={100} value={values.employerCity} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div>
                    <label htmlFor="employerState">State</label>
                    <StateSelect id="employerState" name="employerState" value={values.employerState} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div>
                    <label htmlFor="employerZipCode">Zip</label>
                    <input id="employerZipCode" name="employerZipCode" type="text" inputMode="decimal" maxLength={5} value={values.employerZipCode} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                </div>
                <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="employedYears">Years with Employer</label>
                    <input id="employedYears" name="employedYears" type="text" inputMode="decimal" className={ic('employedYears')} value={values.employedYears} onChange={handleChange} onBlur={handleBlur} />
                    {err('employedYears')}
                  </div>
                  <div>
                    <label htmlFor="employedMonths">Months with Employer</label>
                    <input id="employedMonths" name="employedMonths" type="text" inputMode="decimal" maxLength={2} className={ic('employedMonths')} value={values.employedMonths} onChange={handleChange} onBlur={handleBlur} />
                    {err('employedMonths')}
                  </div>
                </div>

                <h4 className="asf-mini-section">Previous Employer <span className="asf-hint">(required if less than 2 years with the current employer above)</span></h4>
                <div className="asf-grid asf-cols-2">
                  <div>
                    <label htmlFor="previousEmployerName">Previous Employer Name</label>
                    <input id="previousEmployerName" name="previousEmployerName" type="text" maxLength={100} value={values.previousEmployerName} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div>
                    <label htmlFor="previousOccupationTitle">Previous Occupation / Title</label>
                    <input id="previousOccupationTitle" name="previousOccupationTitle" type="text" value={values.previousOccupationTitle} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                </div>
                <div className="asf-grid asf-cols-3" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="previousGrossMonthlyIncome">Previous Gross Monthly Income</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="previousGrossMonthlyIncome" name="previousGrossMonthlyIncome" type="text" inputMode="decimal" value={values.previousGrossMonthlyIncome} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="previousEmployedYears">Years</label>
                    <input id="previousEmployedYears" name="previousEmployedYears" type="text" inputMode="decimal" className={ic('previousEmployedYears')} value={values.previousEmployedYears} onChange={handleChange} onBlur={handleBlur} />
                    {err('previousEmployedYears')}
                  </div>
                  <div>
                    <label htmlFor="previousEmployedMonths">Months</label>
                    <input id="previousEmployedMonths" name="previousEmployedMonths" type="text" inputMode="decimal" maxLength={2} className={ic('previousEmployedMonths')} value={values.previousEmployedMonths} onChange={handleChange} onBlur={handleBlur} />
                    {err('previousEmployedMonths')}
                  </div>
                </div>

                <h3 className="asf-subsection">Additional Income</h3>
                <div className="asf-grid asf-cols-2">
                  <div>
                    <label htmlFor="additionalIncomeSource">Source</label>
                    <select id="additionalIncomeSource" name="additionalIncomeSource" value={values.additionalIncomeSource} onChange={handleChange} onBlur={handleBlur}>
                      <option value="">Choose</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Self-Employment">Self-Employment</option>
                      <option value="Investments">Investments</option>
                      <option value="Pension">Pension</option>
                      <option value="Child Support / Alimony">Child Support / Alimony</option>
                      <option value="Social Security">Social Security</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="additionalMonthlyIncome">Monthly Amount</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="additionalMonthlyIncome" name="additionalMonthlyIncome" type="text" inputMode="decimal" value={values.additionalMonthlyIncome} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                  </div>
                </div>
                {values.additionalIncomeSource === 'Other' && (
                  <div className="asf-grid" style={{ marginTop: 14 }}>
                    <div>
                      <label htmlFor="additionalIncomeSourceOther">Please describe</label>
                      <input id="additionalIncomeSourceOther" name="additionalIncomeSourceOther" type="text" value={values.additionalIncomeSourceOther} onChange={handleChange} onBlur={handleBlur} />
                    </div>
                  </div>
                )}
                <p className="asf-form-note">Disclosure: Alimony, child support, or separate maintenance income need not be revealed if the applicant does not wish to have it considered as a basis for repaying this obligation.</p>
              </div>

              {/* ── Co-Buyer Toggle ── */}
              <div className="asf-card">
                <h2 className="asf-section">Co-Buyer</h2>
                <div className="asf-form-question">Does this buyer have a Co-Buyer?</div>
                <div className="asf-radio-card-group">
                  <label className={`asf-radio-card${values.haveCoApplicant ? ' asf-radio-card--checked' : ''}`} htmlFor="haveCoApplicant-yes">
                    <input id="haveCoApplicant-yes" name="haveCoApplicant" type="radio" value="true" checked={values.haveCoApplicant === true} onChange={() => setFieldValue('haveCoApplicant', true)} />
                    <span className="asf-label-text">Yes</span>
                  </label>
                  <label className={`asf-radio-card${!values.haveCoApplicant ? ' asf-radio-card--checked' : ''}`} htmlFor="haveCoApplicant-no">
                    <input id="haveCoApplicant-no" name="haveCoApplicant" type="radio" value="false" checked={values.haveCoApplicant === false} onChange={() => setFieldValue('haveCoApplicant', false)} />
                    <span className="asf-label-text">No</span>
                  </label>
                </div>
              </div>

              {/* ── Co-Buyer Block ── */}
              {values.haveCoApplicant && (
                <>
                  <div className="asf-card asf-cobuyer-card">
                    <h2 className="asf-section">Co-Buyer Information</h2>
                    <div className="asf-grid asf-cols-4">
                      <div>
                        <label htmlFor="coapplicantFirstName">First Name</label>
                        <input id="coapplicantFirstName" name="coapplicantFirstName" type="text" required value={values.coapplicantFirstName} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantMiddleName">Middle Name</label>
                        <input id="coapplicantMiddleName" name="coapplicantMiddleName" type="text" value={values.coapplicantMiddleName} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantLastName">Last Name</label>
                        <input id="coapplicantLastName" name="coapplicantLastName" type="text" required value={values.coapplicantLastName} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantSuffix">Suffix</label>
                        <select id="coapplicantSuffix" name="coapplicantSuffix" value={values.coapplicantSuffix} onChange={handleChange} onBlur={handleBlur}>
                          <option value="">Choose</option>
                          <option>Jr.</option><option>Sr.</option><option>II</option><option>III</option><option>IV</option><option>V</option>
                        </select>
                      </div>
                    </div>
                    <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantPrimaryPhoneNumber">Phone Number</label>
                        <div className="asf-input-group">
                          <span className="asf-input-prefix">+1</span>
                          <input id="coapplicantPrimaryPhoneNumber" name="coapplicantPrimaryPhoneNumber" type="tel" required value={values.coapplicantPrimaryPhoneNumber} onChange={phoneChange} onBlur={handleBlur} />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="coapplicantPrimaryPhoneType">Phone Number Type</label>
                        <select id="coapplicantPrimaryPhoneType" name="coapplicantPrimaryPhoneType" required value={values.coapplicantPrimaryPhoneType} onChange={handleChange} onBlur={handleBlur}>
                          <option value="">Choose</option>
                          <option value="Mobile">Mobile</option>
                          <option value="Home">Home</option>
                        </select>
                      </div>
                    </div>
                    <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantEmailAddress">E-Mail Address</label>
                        <input id="coapplicantEmailAddress" name="coapplicantEmailAddress" type="email" required value={values.coapplicantEmailAddress} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantMaritalStatus">Marital Status</label>
                        <select id="coapplicantMaritalStatus" name="coapplicantMaritalStatus" required value={values.coapplicantMaritalStatus} onChange={handleChange} onBlur={handleBlur}>
                          <option value="">Choose</option>
                          <option value="Single">Single</option>
                          <option value="Married">Married</option>
                          <option value="Separated">Separated</option>
                        </select>
                      </div>
                    </div>
                    <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantSsn">SSN</label>
                        <input id="coapplicantSsn" name="coapplicantSsn" type="password" maxLength={11} className={ic('coapplicantSsn')} required value={values.coapplicantSsn} onChange={ssnChange} onBlur={handleBlur} />
                        {err('coapplicantSsn')}
                      </div>
                      <div>
                        <label htmlFor="coapplicantBirthdate">Birth Date</label>
                        <input id="coapplicantBirthdate" name="coapplicantBirthdate" type="date" min={BIRTHDATE_MIN} max={BIRTHDATE_MAX} className={ic('coapplicantBirthdate')} required value={values.coapplicantBirthdate} onChange={handleChange} onBlur={handleBlur} />
                        {err('coapplicantBirthdate')}
                      </div>
                    </div>
                  </div>

                  <div className="asf-card asf-cobuyer-card">
                    <h2 className="asf-section">Co-Buyer Residential Information</h2>
                    <div className="asf-grid">
                      <div>
                        <label htmlFor="coapplicantResidentialAddressLine1">Physical Address</label>
                        <input id="coapplicantResidentialAddressLine1" name="coapplicantResidentialAddressLine1" type="text" required value={values.coapplicantResidentialAddressLine1} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                    </div>
                    <div className="asf-grid asf-cols-3" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantResidentialCity">City</label>
                        <input id="coapplicantResidentialCity" name="coapplicantResidentialCity" type="text" required value={values.coapplicantResidentialCity} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantResidentialState">State</label>
                        <StateSelect id="coapplicantResidentialState" name="coapplicantResidentialState" required value={values.coapplicantResidentialState} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantResidentialZipCode">Zip</label>
                        <input id="coapplicantResidentialZipCode" name="coapplicantResidentialZipCode" type="text" inputMode="decimal" maxLength={5} required value={values.coapplicantResidentialZipCode} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                    </div>
                    <h3 className="asf-subsection">Time at Residence</h3>
                    <div className="asf-grid asf-cols-4">
                      <div>
                        <label htmlFor="coapplicantYearsAtResidentialAddress">Years</label>
                        <input id="coapplicantYearsAtResidentialAddress" name="coapplicantYearsAtResidentialAddress" type="text" inputMode="decimal" required value={values.coapplicantYearsAtResidentialAddress} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantMonthsAtResidentialAddress">Months</label>
                        <input id="coapplicantMonthsAtResidentialAddress" name="coapplicantMonthsAtResidentialAddress" type="text" inputMode="decimal" maxLength={2} required value={values.coapplicantMonthsAtResidentialAddress} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantStatusAtResidentialAddress">Status</label>
                        <select id="coapplicantStatusAtResidentialAddress" name="coapplicantStatusAtResidentialAddress" required value={values.coapplicantStatusAtResidentialAddress} onChange={handleChange} onBlur={handleBlur}>
                          <option value="">Choose</option>
                          <option value="Own/Buying">Own/Buying</option>
                          <option value="Rent">Rent</option>
                          <option value="with Relatives">with Relatives</option>
                          <option value="with Friends">with Friends</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="coapplicantMonthlyPayment">Mortgage/Rent Amount</label>
                        <div className="asf-input-group">
                          <span className="asf-input-prefix">$</span>
                          <input id="coapplicantMonthlyPayment" name="coapplicantMonthlyPayment" type="text" inputMode="decimal" required value={values.coapplicantMonthlyPayment} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                        </div>
                      </div>
                    </div>
                    <h4 className="asf-mini-section">Previous Address <span className="asf-hint">(required if less than 2 years at the co-buyer's current address above)</span></h4>
                    <div className="asf-grid">
                      <div>
                        <label htmlFor="coapplicantPreviousAddressLine1">Previous Address</label>
                        <input id="coapplicantPreviousAddressLine1" name="coapplicantPreviousAddressLine1" type="text" value={values.coapplicantPreviousAddressLine1} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                    </div>
                    <div className="asf-grid asf-cols-3" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantPreviousCity">City</label>
                        <input id="coapplicantPreviousCity" name="coapplicantPreviousCity" type="text" value={values.coapplicantPreviousCity} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantPreviousState">State</label>
                        <StateSelect id="coapplicantPreviousState" name="coapplicantPreviousState" value={values.coapplicantPreviousState} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantPreviousZipCode">Zip</label>
                        <input id="coapplicantPreviousZipCode" name="coapplicantPreviousZipCode" type="text" inputMode="decimal" maxLength={5} value={values.coapplicantPreviousZipCode} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                    </div>
                    <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantPreviousYearsAtAddress">Years at Previous</label>
                        <input id="coapplicantPreviousYearsAtAddress" name="coapplicantPreviousYearsAtAddress" type="text" inputMode="decimal" className={ic('coapplicantPreviousYearsAtAddress')} value={values.coapplicantPreviousYearsAtAddress} onChange={handleChange} onBlur={handleBlur} />
                        {err('coapplicantPreviousYearsAtAddress')}
                      </div>
                      <div>
                        <label htmlFor="coapplicantPreviousMonthsAtAddress">Months at Previous</label>
                        <input id="coapplicantPreviousMonthsAtAddress" name="coapplicantPreviousMonthsAtAddress" type="text" inputMode="decimal" maxLength={2} className={ic('coapplicantPreviousMonthsAtAddress')} value={values.coapplicantPreviousMonthsAtAddress} onChange={handleChange} onBlur={handleBlur} />
                        {err('coapplicantPreviousMonthsAtAddress')}
                      </div>
                    </div>
                  </div>

                  <div className="asf-card asf-cobuyer-card">
                    <h2 className="asf-section">Co-Buyer Employment Information</h2>
                    <p className="asf-form-note">Fill in any sections that apply to the co-buyer. At least one income source is required.</p>

                    <h3 className="asf-subsection">Retirement / Disability Income</h3>
                    <div className="asf-grid asf-cols-3">
                      <div>
                        <label htmlFor="coapplicantRetirementIncome">Monthly Income</label>
                        <div className="asf-input-group">
                          <span className="asf-input-prefix">$</span>
                          <input id="coapplicantRetirementIncome" name="coapplicantRetirementIncome" type="text" inputMode="decimal" value={values.coapplicantRetirementIncome} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="coapplicantRetirementYears">Years Receiving</label>
                        <input id="coapplicantRetirementYears" name="coapplicantRetirementYears" type="text" inputMode="decimal" className={ic('coapplicantRetirementYears')} value={values.coapplicantRetirementYears} onChange={handleChange} onBlur={handleBlur} />
                        {err('coapplicantRetirementYears')}
                      </div>
                      <div>
                        <label htmlFor="coapplicantRetirementMonths">Months Receiving</label>
                        <input id="coapplicantRetirementMonths" name="coapplicantRetirementMonths" type="text" inputMode="decimal" maxLength={2} className={ic('coapplicantRetirementMonths')} value={values.coapplicantRetirementMonths} onChange={handleChange} onBlur={handleBlur} />
                        {err('coapplicantRetirementMonths')}
                      </div>
                    </div>

                    <h3 className="asf-subsection">Current Employment</h3>
                    <div className="asf-grid asf-cols-2">
                      <div>
                        <label htmlFor="coapplicantEmployerName">Employer Name</label>
                        <input id="coapplicantEmployerName" name="coapplicantEmployerName" type="text" maxLength={100} value={values.coapplicantEmployerName} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantEmployerPhoneNumber">Employer Phone</label>
                        <div className="asf-input-group">
                          <span className="asf-input-prefix">+1</span>
                          <input id="coapplicantEmployerPhoneNumber" name="coapplicantEmployerPhoneNumber" type="tel" value={values.coapplicantEmployerPhoneNumber} onChange={phoneChange} onBlur={handleBlur} />
                        </div>
                      </div>
                    </div>
                    <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantOccupationTitle">Occupation / Title</label>
                        <input id="coapplicantOccupationTitle" name="coapplicantOccupationTitle" type="text" value={values.coapplicantOccupationTitle} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantGrossMonthlyIncome">Gross Monthly Income</label>
                        <div className="asf-input-group">
                          <span className="asf-input-prefix">$</span>
                          <input id="coapplicantGrossMonthlyIncome" name="coapplicantGrossMonthlyIncome" type="text" inputMode="decimal" value={values.coapplicantGrossMonthlyIncome} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                        </div>
                      </div>
                    </div>
                    <div className="asf-grid" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantEmployerAddressLine1">Employer Address</label>
                        <input id="coapplicantEmployerAddressLine1" name="coapplicantEmployerAddressLine1" type="text" maxLength={100} value={values.coapplicantEmployerAddressLine1} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                    </div>
                    <div className="asf-grid asf-cols-3" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantEmployerCity">City</label>
                        <input id="coapplicantEmployerCity" name="coapplicantEmployerCity" type="text" maxLength={100} value={values.coapplicantEmployerCity} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantEmployerState">State</label>
                        <StateSelect id="coapplicantEmployerState" name="coapplicantEmployerState" value={values.coapplicantEmployerState} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantEmployerZipCode">Zip</label>
                        <input id="coapplicantEmployerZipCode" name="coapplicantEmployerZipCode" type="text" inputMode="decimal" maxLength={5} value={values.coapplicantEmployerZipCode} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                    </div>
                    <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantEmployedYears">Years with Employer</label>
                        <input id="coapplicantEmployedYears" name="coapplicantEmployedYears" type="text" inputMode="decimal" className={ic('coapplicantEmployedYears')} value={values.coapplicantEmployedYears} onChange={handleChange} onBlur={handleBlur} />
                        {err('coapplicantEmployedYears')}
                      </div>
                      <div>
                        <label htmlFor="coapplicantEmployedMonths">Months with Employer</label>
                        <input id="coapplicantEmployedMonths" name="coapplicantEmployedMonths" type="text" inputMode="decimal" maxLength={2} className={ic('coapplicantEmployedMonths')} value={values.coapplicantEmployedMonths} onChange={handleChange} onBlur={handleBlur} />
                        {err('coapplicantEmployedMonths')}
                      </div>
                    </div>
                    <h4 className="asf-mini-section">Previous Employer <span className="asf-hint">(required if less than 2 years with the co-buyer's current employer above)</span></h4>
                    <div className="asf-grid asf-cols-2">
                      <div>
                        <label htmlFor="coapplicantPreviousEmployerName">Previous Employer Name</label>
                        <input id="coapplicantPreviousEmployerName" name="coapplicantPreviousEmployerName" type="text" maxLength={100} value={values.coapplicantPreviousEmployerName} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                      <div>
                        <label htmlFor="coapplicantPreviousOccupationTitle">Previous Occupation / Title</label>
                        <input id="coapplicantPreviousOccupationTitle" name="coapplicantPreviousOccupationTitle" type="text" value={values.coapplicantPreviousOccupationTitle} onChange={handleChange} onBlur={handleBlur} />
                      </div>
                    </div>
                    <div className="asf-grid asf-cols-3" style={{ marginTop: 14 }}>
                      <div>
                        <label htmlFor="coapplicantPreviousGrossMonthlyIncome">Previous Gross Monthly Income</label>
                        <div className="asf-input-group">
                          <span className="asf-input-prefix">$</span>
                          <input id="coapplicantPreviousGrossMonthlyIncome" name="coapplicantPreviousGrossMonthlyIncome" type="text" inputMode="decimal" value={values.coapplicantPreviousGrossMonthlyIncome} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="coapplicantPreviousEmployedYears">Years</label>
                        <input id="coapplicantPreviousEmployedYears" name="coapplicantPreviousEmployedYears" type="text" inputMode="decimal" className={ic('coapplicantPreviousEmployedYears')} value={values.coapplicantPreviousEmployedYears} onChange={handleChange} onBlur={handleBlur} />
                        {err('coapplicantPreviousEmployedYears')}
                      </div>
                      <div>
                        <label htmlFor="coapplicantPreviousEmployedMonths">Months</label>
                        <input id="coapplicantPreviousEmployedMonths" name="coapplicantPreviousEmployedMonths" type="text" inputMode="decimal" maxLength={2} className={ic('coapplicantPreviousEmployedMonths')} value={values.coapplicantPreviousEmployedMonths} onChange={handleChange} onBlur={handleBlur} />
                        {err('coapplicantPreviousEmployedMonths')}
                      </div>
                    </div>

                    <h3 className="asf-subsection">Additional Income</h3>
                    <div className="asf-grid asf-cols-2">
                      <div>
                        <label htmlFor="coapplicantAdditionalIncomeSource">Source</label>
                        <select id="coapplicantAdditionalIncomeSource" name="coapplicantAdditionalIncomeSource" value={values.coapplicantAdditionalIncomeSource} onChange={handleChange} onBlur={handleBlur}>
                          <option value="">Choose</option>
                          <option value="Spouse">Spouse</option>
                          <option value="Self-Employment">Self-Employment</option>
                          <option value="Investments">Investments</option>
                          <option value="Pension">Pension</option>
                          <option value="Child Support / Alimony">Child Support / Alimony</option>
                          <option value="Social Security">Social Security</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="coapplicantAdditionalMonthlyIncome">Monthly Amount</label>
                        <div className="asf-input-group">
                          <span className="asf-input-prefix">$</span>
                          <input id="coapplicantAdditionalMonthlyIncome" name="coapplicantAdditionalMonthlyIncome" type="text" inputMode="decimal" value={values.coapplicantAdditionalMonthlyIncome} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                        </div>
                      </div>
                    </div>
                    {values.coapplicantAdditionalIncomeSource === 'Other' && (
                      <div className="asf-grid" style={{ marginTop: 14 }}>
                        <div>
                          <label htmlFor="coapplicantAdditionalIncomeSourceOther">Please describe</label>
                          <input id="coapplicantAdditionalIncomeSourceOther" name="coapplicantAdditionalIncomeSourceOther" type="text" value={values.coapplicantAdditionalIncomeSourceOther} onChange={handleChange} onBlur={handleBlur} />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Purchase Information ── */}
              <div className="asf-card">
                <h2 className="asf-section">Purchase Information</h2>

                <div className="asf-grid asf-cols-3">
                  <div>
                    <label htmlFor="partner">Partner</label>
                    <input id="partner" name="partner" type="text" readOnly value={values.partner} onChange={handleChange} />
                  </div>
                  <div>
                    <label htmlFor="partnerRep">{partnerLabel ? `${partnerLabel} Rep` : 'Partner Rep'}</label>
                    <input id="partnerRep" name="partnerRep" type="text" className={ic('partnerRep')} required={!!partnerLabel} value={values.partnerRep} onChange={handleChange} onBlur={handleBlur} />
                    {err('partnerRep')}
                  </div>
                  <div>
                    <label htmlFor="partnerRepEmail">{partnerLabel ? `${partnerLabel} Rep Email` : 'Partner Rep Email'}</label>
                    <input id="partnerRepEmail" name="partnerRepEmail" type="email" className={ic('partnerRepEmail')} required={!!partnerLabel} autoComplete="off" value={values.partnerRepEmail} onChange={handleChange} onBlur={handleBlur} />
                    {err('partnerRepEmail')}
                  </div>
                </div>

                <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="sellerType">Seller Type</label>
                    <select id="sellerType" name="sellerType" className={ic('sellerType')} required value={values.sellerType} onChange={handleChange} onBlur={handleBlur}>
                      <option value="">Choose</option>
                      <option value="Dealer">Dealer</option>
                      <option value="Private Party">Private Party</option>
                    </select>
                    {err('sellerType')}
                  </div>
                  <div />
                </div>

                {values.sellerType && (
                  <div className="asf-conditional" style={{ marginTop: 14 }}>
                    <div className={`asf-grid ${values.sellerType === 'Dealer' ? 'asf-cols-3' : 'asf-cols-2'}`}>
                      <div>
                        <label htmlFor="sellerName">{values.sellerType === 'Dealer' ? 'Dealer Name' : 'Seller Name'}</label>
                        <input id="sellerName" name="sellerName" type="text" className={ic('sellerName')} required value={values.sellerName} onChange={(e) => { handleChange(e); if (values.sellerType === 'Dealer') setFieldValue('dealerName', e.target.value); }} onBlur={handleBlur} />
                        {err('sellerName')}
                      </div>
                      <div>
                        <label htmlFor="sellerPhoneNumber">Phone Number</label>
                        <input id="sellerPhoneNumber" name="sellerPhoneNumber" type="tel" value={values.sellerPhoneNumber} onChange={phoneChange} onBlur={handleBlur} />
                      </div>
                      {values.sellerType === 'Dealer' && (
                        <div>
                          <label htmlFor="sellerContact">Salesperson / Contact</label>
                          <input id="sellerContact" name="sellerContact" type="text" value={values.sellerContact} onChange={handleChange} onBlur={handleBlur} />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <h3 className="asf-subsection">Collateral</h3>
                <div className="asf-grid asf-cols-2">
                  <div>
                    <label htmlFor="collateralType">Collateral Type</label>
                    <select id="collateralType" name="collateralType" className={ic('collateralType')} required value={values.collateralType} onChange={handleChange} onBlur={handleBlur}>
                      <option value="">Choose</option>
                      <option>Cargo/Utility</option><option>Dump</option><option>Flatbed</option>
                      <option>Car Hauler</option><option>Mini Camper</option><option>Boat Trailers</option>
                      <option>RV</option><option>Motorhome</option><option>Travel Trailer</option>
                      <option>Toy Hauler</option><option>Livestock</option><option>Horse Trailers</option>
                      <option>Marine</option><option>Personal Watercraft</option><option>Powersports</option>
                    </select>
                    {err('collateralType')}
                  </div>
                  <div>
                    <label>Unit Status <span className="asf-hint">(is the unit on the lot or still being built?)</span></label>
                    <div className="asf-radio-row">
                      <label>
                        <input type="radio" name="unitStatus" value="In Stock" checked={values.unitStatus === 'In Stock'} onChange={() => setFieldValue('unitStatus', 'In Stock')} />
                        Already In Stock
                      </label>
                      <label>
                        <input type="radio" name="unitStatus" value="Build Unit" checked={values.unitStatus === 'Build Unit'} onChange={() => setFieldValue('unitStatus', 'Build Unit')} />
                        Being Built / Ordered
                      </label>
                    </div>
                  </div>
                </div>

                <div className="asf-grid asf-cols-3" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="vehicleYear">Year</label>
                    <input id="vehicleYear" name="vehicleYear" type="text" inputMode="numeric" maxLength={4} className={ic('vehicleYear')} required value={values.vehicleYear} onChange={handleChange} onBlur={handleBlur} />
                    {err('vehicleYear')}
                  </div>
                  <div>
                    <label htmlFor="vehicleMake">Make</label>
                    <input id="vehicleMake" name="vehicleMake" type="text" className={ic('vehicleMake')} required value={values.vehicleMake} onChange={handleChange} onBlur={handleBlur} />
                    {err('vehicleMake')}
                  </div>
                  <div>
                    <label htmlFor="vehicleModel">Model</label>
                    <input id="vehicleModel" name="vehicleModel" type="text" className={ic('vehicleModel')} required value={values.vehicleModel} onChange={handleChange} onBlur={handleBlur} />
                    {err('vehicleModel')}
                  </div>
                </div>

                <h3 className="asf-subsection">Pricing &amp; Back-End Products</h3>
                <div className="asf-grid asf-cols-2">
                  <div>
                    <label htmlFor="vehicleSalesPrice">Sales Price</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="vehicleSalesPrice" name="vehicleSalesPrice" type="text" inputMode="decimal" className={ic('vehicleSalesPrice')} required value={values.vehicleSalesPrice} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                    {err('vehicleSalesPrice')}
                  </div>
                  <div>
                    <label htmlFor="vehicleDownPayment">Down Payment</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="vehicleDownPayment" name="vehicleDownPayment" type="text" inputMode="decimal" className={ic('vehicleDownPayment')} required value={values.vehicleDownPayment} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                    {err('vehicleDownPayment')}
                  </div>
                </div>
                <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="gps">GPS <span className="asf-hint">(not included in 25% cap)</span></label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="gps" name="gps" type="text" inputMode="decimal" value={values.gps} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="theft">Theft</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="theft" name="theft" type="text" inputMode="decimal" value={values.theft} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                  </div>
                </div>
                <div className="asf-grid asf-cols-2" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="tireWheel">Tire &amp; Wheel</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="tireWheel" name="tireWheel" type="text" inputMode="decimal" value={values.tireWheel} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="vsc">Vehicle Service Contract</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="vsc" name="vsc" type="text" inputMode="decimal" value={values.vsc} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                  </div>
                </div>
                <div className="asf-grid asf-misc-grid" style={{ marginTop: 14 }}>
                  <div>
                    <label htmlFor="miscDescription">Misc. — Description</label>
                    <input id="miscDescription" name="miscDescription" type="text" maxLength={255} value={values.miscDescription} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div>
                    <label htmlFor="miscCost">Misc. — Cost</label>
                    <div className="asf-input-group">
                      <span className="asf-input-prefix">$</span>
                      <input id="miscCost" name="miscCost" type="text" inputMode="decimal" value={values.miscCost} onChange={handleChange} onFocus={moneyFocus} onBlur={moneyBlur} />
                    </div>
                  </div>
                </div>

                {showBackendAlert && (
                  <div className="asf-alert asf-error" style={{ marginTop: 12 }}>
                    <strong>Back-end products exceed 25% of the Sales Price.</strong>
                    {' '}Theft + Tire &amp; Wheel + Vehicle Service Contract + Misc cannot be greater than 25% of the Sales Price. (GPS is not included in this cap.) Please adjust to submit.
                    {' '}Back-end total (excl. GPS): ${backendCapped.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · 25% of Sales Price: ${(salesPrice * 0.25).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.
                  </div>
                )}

                <div className="asf-total-box">
                  <div className="asf-lbl">Total Requested Amount <span className="asf-hint" style={{ marginLeft: 6 }}>auto-calculated</span></div>
                  <div className="asf-val">{totalFmt}</div>
                </div>
              </div>

              {/* ── Additional Information ── */}
              <div className="asf-card">
                <h2 className="asf-section">Additional Information</h2>
                <div className="asf-grid asf-cols-2">
                  <div>
                    <label htmlFor="referralSource">How did you hear about us?</label>
                    <select id="referralSource" name="referralSource" className={ic('referralSource')} required value={values.referralSource} onChange={handleChange} onBlur={handleBlur}>
                      <option value="">Choose</option>
                      <option value="Partner">Partner</option>
                      <option value="Google">Google</option>
                      <option value="Friend/Family">Friend/Family</option>
                      <option value="Dealership">Dealership</option>
                      <option value="Facebook">Facebook</option>
                      <option value="Instagram">Instagram</option>
                      <option value="Returning Customer">Returning Customer</option>
                      <option value="Other">Other</option>
                    </select>
                    {err('referralSource')}
                  </div>
                  {values.referralSource === 'Other' && (
                    <div>
                      <label htmlFor="referralSourceOther">Please describe</label>
                      <input id="referralSourceOther" name="referralSourceOther" type="text" required value={values.referralSourceOther} onChange={handleChange} onBlur={handleBlur} />
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 14 }}>
                  <label htmlFor="notes">Notes</label>
                  <textarea id="notes" name="notes" rows={3} value={values.notes} onChange={handleChange} onBlur={handleBlur} />
                </div>
              </div>

              {/* ── Authorization ── */}
              <div className="asf-card">
                <h2 className="asf-section">Authorization</h2>
                <div className="asf-auth-block">
                  <p>By checking below, each of us (both buyer and co-buyer) verify our intent to apply for joint credit authorization and have reviewed and agree to the <a href={CompanyConfig.privacyUrl} target="_blank" rel="noreferrer">privacy policy</a>.</p>
                  <p>I certify that the above information stated in this form is true and correct and a complete statement of my financial condition.</p>
                  <p>I understand that this form will be kept whether or not credit is approved. You are authorized to share this form with other potential lenders.</p>
                  <p>You and any potential or subsequent creditor are authorized to check my credit and my employment history, to answer questions about your credit experience with me, and to disclose credit information to each other. I further understand that my information is being submitted to a lender(s).</p>
                </div>

                <div className="asf-grid asf-cols-2" style={{ marginTop: 18 }}>
                  <div>
                    <h3 className="asf-subsection" style={{ marginTop: 4 }}>Buyer</h3>
                    <div className="asf-checkbox-stack">
                      <div>
                        <label>
                          <input id="applicantAgree" name="applicantAgree" type="checkbox" checked={values.applicantAgree} onChange={(e) => setFieldValue('applicantAgree', e.target.checked)} />
                          {' '}I agree
                        </label>
                      </div>
                      <div>
                        <label>
                          <input id="usCitizen" name="usCitizen" type="checkbox" checked={values.usCitizen} onChange={(e) => { const checked = e.target.checked; setValues((prev) => ({ ...prev, usCitizen: checked, usResident: checked ? false : prev.usResident })); }} />
                          {' '}I am a U.S. Citizen
                          <Tooltip title={US_CITIZEN_TOOLTIP} placement="top">
                            <InfoIcon style={INFO_ICON_STYLE} />
                          </Tooltip>
                        </label>
                      </div>
                      <div>
                        <label>
                          <input id="usResident" name="usResident" type="checkbox" checked={values.usResident} onChange={(e) => { const checked = e.target.checked; setValues((prev) => ({ ...prev, usResident: checked, usCitizen: checked ? false : prev.usCitizen })); }} />
                          {' '}I am a Lawful Permanent Resident
                          <Tooltip title={US_RESIDENT_TOOLTIP} placement="top">
                            <InfoIcon style={INFO_ICON_STYLE} />
                          </Tooltip>
                        </label>
                      </div>
                    </div>
                    {submitCount > 0 && (errors.applicantAgree || errors.usResident) && (
                      <div className="asf-auth-error" style={{ marginTop: 8 }}>
                        {errors.applicantAgree && <div className="asf-field-error">{String(errors.applicantAgree)}</div>}
                        {errors.usResident && <div className="asf-field-error">{String(errors.usResident)}</div>}
                      </div>
                    )}
                    <div style={{ marginTop: 10 }}>
                      <label htmlFor="applicantSignature">Buyer Signature</label>
                      <input
                        id="applicantSignature" name="applicantSignature" type="text"
                        className={ic('applicantSignature')}
                        placeholder={values.firstName && values.lastName ? `Type "${values.firstName} ${values.lastName}"` : "Type the buyer's full name as entered above"}
                        value={values.applicantSignature} onChange={handleChange} onBlur={handleBlur}
                      />
                      {err('applicantSignature')}
                    </div>
                  </div>

                  {values.haveCoApplicant && (
                    <div>
                      <h3 className="asf-subsection" style={{ marginTop: 4 }}>Co-Buyer</h3>
                      <div className="asf-checkbox-stack">
                        <div>
                          <label>
                            <input id="coApplicantAgree" name="coApplicantAgree" type="checkbox" checked={values.coApplicantAgree} onChange={(e) => setFieldValue('coApplicantAgree', e.target.checked)} />
                            {' '}I agree
                          </label>
                        </div>
                        <div>
                          <label>
                            <input id="coApplicantUsCitizen" name="coApplicantUsCitizen" type="checkbox" checked={values.coApplicantUsCitizen} onChange={(e) => { const checked = e.target.checked; setValues((prev) => ({ ...prev, coApplicantUsCitizen: checked, coApplicantUsResident: checked ? false : prev.coApplicantUsResident })); }} />
                            {' '}I am a U.S. Citizen
                            <Tooltip title={US_CITIZEN_TOOLTIP} placement="top">
                              <InfoIcon style={INFO_ICON_STYLE} />
                            </Tooltip>
                          </label>
                        </div>
                        <div>
                          <label>
                            <input id="coApplicantUsResident" name="coApplicantUsResident" type="checkbox" checked={values.coApplicantUsResident} onChange={(e) => { const checked = e.target.checked; setValues((prev) => ({ ...prev, coApplicantUsResident: checked, coApplicantUsCitizen: checked ? false : prev.coApplicantUsCitizen })); }} />
                            {' '}I am a Lawful Permanent Resident
                            <Tooltip title={US_RESIDENT_TOOLTIP} placement="top">
                              <InfoIcon style={INFO_ICON_STYLE} />
                            </Tooltip>
                          </label>
                        </div>
                      </div>
                      {submitCount > 0 && (errors.coApplicantAgree || errors.coApplicantUsResident) && (
                        <div className="asf-auth-error" style={{ marginTop: 8 }}>
                          {errors.coApplicantAgree && <div className="asf-field-error">{String(errors.coApplicantAgree)}</div>}
                          {errors.coApplicantUsResident && <div className="asf-field-error">{String(errors.coApplicantUsResident)}</div>}
                        </div>
                      )}
                      <div style={{ marginTop: 10 }}>
                        <label htmlFor="coApplicantSignature">Co-Buyer Signature</label>
                        <input
                          id="coApplicantSignature" name="coApplicantSignature" type="text"
                          className={ic('coApplicantSignature')}
                          placeholder={values.coapplicantFirstName && values.coapplicantLastName ? `Type "${values.coapplicantFirstName} ${values.coapplicantLastName}"` : "Type the co-buyer's full name as entered above"}
                          value={values.coApplicantSignature} onChange={handleChange} onBlur={handleBlur}
                        />
                        {err('coApplicantSignature')}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Submit ── */}
              <div className="asf-submit-strip">
                <p>Upon submission, the buyer (and co-buyer, if applicable) will receive a confirmation email within 10 minutes. If the buyer doesn't see it, please advise them to check their spam folder. If it's still not there, give us a call at 800-224-8180.</p>
                <button type="submit" className="asf-btn" disabled={showBackendAlert || isCreditAppSubmitting}>
                  {isCreditAppSubmitting ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" style={{ marginRight: 5 }} />
                      Submitting...
                    </>
                  ) : 'Submit'}
                </button>
              </div>

              {isDev && (
                <div style={{ marginTop: 24 }}>
                  <p>Values:</p>
                  <code>{JSON.stringify(values, null, 2)}</code>
                </div>
              )}
            </Form>

            {/* ── Review & Confirm Modal ── */}
            {showReview && (
              <div className="asf-review-modal">
                <div className="asf-rv-overlay" onClick={() => setShowReview(false)} />
                <div className="asf-rv-dialog" role="dialog" aria-modal="true">
                  <div className="asf-rv-header">
                    <h2>Review &amp; confirm</h2>
                    <p>Please scan every field below before submitting. If anything looks wrong or missing, click <strong>Go Back &amp; Edit</strong> to return to the form.</p>
                  </div>
                  <div className="asf-rv-content">
                    <ReviewContent values={pendingSubmitValuesRef.current as any} />
                  </div>
                  <div className="asf-rv-actions">
                    <button type="button" className="asf-rv-btn asf-rv-btn-secondary" onClick={() => setShowReview(false)}>Go Back &amp; Edit</button>
                    <button type="button" className="asf-rv-btn asf-rv-btn-primary" onClick={handleConfirmSubmit}>Confirm &amp; Submit</button>
                  </div>
                </div>
              </div>
            )}

          </div>
        );
      }}
    </Formik>
  );
}

function ReviewContent({ values }: { values: FormValues | null }) {
  if (!values) return null;

  const fmt = (v: string | boolean | undefined) => {
    if (v === undefined || v === null || v === '') return null;
    if (typeof v === 'boolean') return v ? 'Yes' : null;
    return String(v).trim() || null;
  };

  const money = (v: string | undefined) => {
    const n = parseNum(v ?? '');
    return n > 0 ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;
  };

  const Row = ({ label, value }: { label: string; value: string | null }) =>
    value ? (
      <div className="asf-rv-row">
        <dt>{label}</dt>
        <dd>{value}</dd>
      </div>
    ) : null;

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="asf-rv-sec">
      <h3 className="asf-rv-sec-title">{title}</h3>
      <dl className="asf-rv-dl">{children}</dl>
    </section>
  );

  return (
    <>
      <Section title="Buyer Information">
        <Row label="Name" value={[values.firstName, values.middleName, values.lastName, values.suffix].filter(Boolean).join(' ')} />
        <Row label="Phone" value={fmt(values.primaryPhoneNumber)} />
        <Row label="Phone Type" value={fmt(values.primaryPhoneType)} />
        <Row label="Email" value={fmt(values.emailAddress)} />
        <Row label="Marital Status" value={fmt(values.maritalStatus)} />
        <Row label="Birth Date" value={fmt(values.birthdate)} />
      </Section>
      <Section title="Residential Information">
        <Row label="Address" value={fmt(values.residentialAddressLine1)} />
        <Row label="City / State / Zip" value={[values.residentialCity, values.residentialState, values.residentialZipCode].filter(Boolean).join(', ')} />
        <Row label="Time at Address" value={`${values.yearsAtResidentialAddress || '0'} yr ${values.monthsAtResidentialAddress || '0'} mo`} />
        <Row label="Status" value={fmt(values.statusAtResidentialAddress)} />
        <Row label="Monthly Payment" value={money(values.monthlyPayment)} />
      </Section>
      <Section title="Employment Information">
        <Row label="Retirement Income" value={money(values.retirementIncome)} />
        <Row label="Employer" value={fmt(values.employerName)} />
        <Row label="Title" value={fmt(values.occupationTitle)} />
        <Row label="Gross Monthly Income" value={money(values.grossMonthlyIncome)} />
        <Row label="Time with Employer" value={`${values.employedYears || '0'} yr ${values.employedMonths || '0'} mo`} />
        <Row label="Additional Income Source" value={fmt(values.additionalIncomeSource)} />
        <Row label="Additional Monthly Income" value={money(values.additionalMonthlyIncome)} />
      </Section>
      {values.haveCoApplicant && (
        <Section title="Co-Buyer Information">
          <Row label="Name" value={[values.coapplicantFirstName, values.coapplicantLastName].filter(Boolean).join(' ')} />
          <Row label="Phone" value={fmt(values.coapplicantPrimaryPhoneNumber)} />
          <Row label="Email" value={fmt(values.coapplicantEmailAddress)} />
        </Section>
      )}
      <Section title="Purchase Information">
        <Row label="Partner" value={fmt(values.partner) || 'Direct'} />
        <Row label="Partner Rep" value={fmt(values.partnerRep)} />
        <Row label="Seller Type" value={fmt(values.sellerType)} />
        <Row label="Seller" value={fmt(values.sellerName)} />
        <Row label="Collateral Type" value={fmt(values.collateralType)} />
        <Row label="Unit Status" value={fmt(values.unitStatus)} />
        <Row label="Year / Make / Model" value={[values.vehicleYear, values.vehicleMake, values.vehicleModel].filter(Boolean).join(' ')} />
        <Row label="Sales Price" value={money(values.vehicleSalesPrice)} />
        <Row label="Down Payment" value={money(values.vehicleDownPayment)} />
        <Row label="GPS" value={money(values.gps)} />
        <Row label="Theft" value={money(values.theft)} />
        <Row label="Tire & Wheel" value={money(values.tireWheel)} />
        <Row label="Vehicle Service Contract" value={money(values.vsc)} />
        <Row label="Misc" value={money(values.miscCost)} />
      </Section>
      <Section title="Additional Information">
        <Row label="How did you hear about us?" value={fmt(values.referralSource)} />
        <Row label="Notes" value={fmt(values.notes)} />
      </Section>
    </>
  );
}
